import { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import { Link } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import Layout from "@/components/Layout";
import "leaflet/dist/leaflet.css";

function StatTile({ label, value, testid }) {
  return (
    <div className="border border-white/10 bg-[#0a0a0a] px-5 py-6 text-center" data-testid={testid}>
      <div className="font-display-saira text-3xl text-[#C9A055]">{value?.toLocaleString?.() ?? value}</div>
      <div className="font-ui text-[10px] tracking-[0.32em] text-[#8a857d] mt-2 uppercase">{label}</div>
    </div>
  );
}

export default function Registry() {
  const [stats, setStats] = useState({});
  const [mapPoints, setMapPoints] = useState([]);
  const [recent, setRecent] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    Promise.all([
      http.get("/registry/stats").then((r) => setStats(r.data)).catch(() => {}),
      http.get("/registry/map").then((r) => setMapPoints(r.data)).catch(() => {}),
      http.get("/registry/recent").then((r) => setRecent(r.data)).catch(() => {}),
      http.get("/registry/featured?limit=5").then((r) => setFeatured(r.data)).catch(() => {}),
    ]);
  }, []);

  return (
    <Layout>
      <section className="bg-[#050505] pt-24 pb-12 border-b border-white/5">
        <div className="container mx-auto px-4 text-center">
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">THE TRUTH REGISTRY™</div>
          <h1 className="font-display text-4xl md:text-6xl text-[#F2EFE8] mt-4">Where The Truths Live</h1>
          <p className="font-body text-[#B5B0A7] max-w-2xl mx-auto mt-5 leading-relaxed">
            A living map of meaningful prints spreading across homes, businesses, and communities around the world. Every truth has a story. Every story has a home.
          </p>
        </div>
      </section>

      {/* Featured Truth Homes — admin-pinned hero strip */}
      {featured.length > 0 && (
        <section className="bg-[#050505] py-14 border-b border-white/5">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">★ FEATURED TRUTH HOMES</div>
              <h2 className="font-display text-3xl md:text-4xl text-[#F2EFE8] mt-2">Truths Worth Celebrating</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="featured-homes">
              {featured.map((t) => (
                <Link key={t.truth_id} to={`/truths/${t.truth_id}`} data-testid={`featured-${t.truth_id}`}
                  className="group block border border-[#C9A055]/30 bg-[#0a0a0a] hover:border-[#C9A055] transition-colors overflow-hidden">
                  {t.artwork_image && (
                    <div className="aspect-square bg-black overflow-hidden">
                      <img src={resolveImage(t.artwork_image)} alt={t.artwork_title}
                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"/>
                    </div>
                  )}
                  <div className="p-3">
                    <div className="font-ui text-[9px] tracking-[0.22em] text-[#C9A055]">{t.truth_id}</div>
                    <div className="font-display text-base text-[#F2EFE8] mt-1 uppercase truncate">{t.artwork_title}</div>
                    <div className="font-body text-[11px] text-[#8a857d] mt-1 truncate">📍 {t.current_home_label}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Stats */}
      <section className="bg-[#050505] py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="registry-stats">
            <StatTile label="Registered Truths" value={stats.registered_truths || 0} testid="stat-truths"/>
            <StatTile label="States Reached" value={stats.states_reached || 0}/>
            <StatTile label="Countries" value={stats.countries_reached || 0}/>
            <StatTile label="Story Reads" value={stats.story_reads || 0}/>
            <StatTile label="QR Scans" value={stats.qr_scans || 0}/>
            <StatTile label="Truth Keepers" value={stats.active_truth_keepers || 0}/>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="bg-[#050505] pb-16">
        <div className="container mx-auto px-4">
          <div className="border border-white/10 overflow-hidden" data-testid="truth-map">
            <MapContainer center={[37.8, -96]} zoom={4} style={{ height: "520px", width: "100%", background: "#0a0a0a" }} scrollWheelZoom={false}>
              <TileLayer
                attribution='&copy; OpenStreetMap'
                url="https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png"
              />
              {mapPoints.map((p) => {
                const isBusiness = p.privacy === "business";
                const isFeatured = p.featured;
                const color = isBusiness ? "#5fc377" : "#C9A055";
                const radius = isFeatured ? 11 : (isBusiness ? 9 : 7);
                return (
                  <CircleMarker
                    key={p.truth_id}
                    center={[p.lat, p.lng]}
                    radius={radius}
                    pathOptions={{ color, fillColor: color, fillOpacity: isFeatured ? 0.95 : 0.7, weight: isFeatured ? 3 : 2 }}
                  >
                    <Popup>
                      <div style={{ fontFamily: "Arial", color: "#0a0a0a", minWidth: 180 }}>
                        {isFeatured && <div style={{ fontSize: 10, color: "#C9A055", letterSpacing: 1, marginBottom: 4 }}>★ FEATURED</div>}
                        <div style={{ fontWeight: 700, letterSpacing: 1 }}>{p.artwork_title?.toUpperCase()}</div>
                        {isBusiness && p.business_name && (
                          <div style={{ fontSize: 12, color: "#5fc377", fontWeight: 600, marginTop: 2 }}>🏛 {p.business_name}</div>
                        )}
                        <div style={{ fontSize: 12 }}>📍 {p.current_home_label}</div>
                        {p.co_owner_display_name && <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>with {p.co_owner_display_name}</div>}
                        <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>Truth ID: {p.truth_id}</div>
                        <a href={`/truths/${p.truth_id}`} style={{ color: "#C9A055", fontSize: 11 }}>Read the story →</a>
                        {isBusiness && p.business_website && (
                          <div style={{ marginTop: 4 }}>
                            <a href={p.business_website} target="_blank" rel="noreferrer" style={{ color: "#5fc377", fontSize: 11 }}>Visit business →</a>
                          </div>
                        )}
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          </div>
          {/* Map legend */}
          <div className="mt-4 flex flex-wrap gap-5 justify-center font-ui text-[10px] tracking-[0.22em] text-[#8a857d] uppercase" data-testid="map-legend">
            <span className="inline-flex items-center gap-2"><span className="inline-block w-3 h-3 rounded-full bg-[#C9A055]"/> Truth Keeper</span>
            <span className="inline-flex items-center gap-2"><span className="inline-block w-3 h-3 rounded-full bg-[#5fc377]"/> Business / Public Home</span>
            <span className="inline-flex items-center gap-2"><span className="inline-block w-4 h-4 rounded-full bg-[#C9A055] border-2 border-white/40"/> Featured</span>
          </div>
        </div>
      </section>

      {/* Recently Found Homes */}
      <section className="bg-[#050505] pb-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">NEW TRUTH HOMES</div>
            <h2 className="font-display text-3xl md:text-4xl text-[#F2EFE8] mt-2">Recently Found Homes</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="recent-homes">
            {recent.length === 0 ? (
              <div className="col-span-full text-center font-body text-[#8a857d] italic py-8">
                No registered truths yet — register your truth to be the first.
              </div>
            ) : recent.map((t) => (
              <Link
                key={t.truth_id}
                to={`/truths/${t.truth_id}`}
                className="block border border-white/10 bg-[#0a0a0a] p-5 hover:border-[#C9A055]/40 transition-colors"
                data-testid={`recent-${t.truth_id}`}
              >
                <div className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055]">{t.truth_id}</div>
                <div className="font-display text-xl text-[#F2EFE8] mt-2 uppercase">{t.artwork_title}</div>
                <div className="font-body text-[13px] text-[#B5B0A7] mt-2">
                  Found a home in <strong className="text-[#C9A055]">{t.current_home_label}</strong>
                </div>
                {t.co_owner_display_name && (
                  <div className="font-body text-[11px] text-[#8a857d] mt-1">with {t.co_owner_display_name}</div>
                )}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
